import React, { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Separator } from "@/components/ui/separator";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Loader2, CheckCircle2, MessageSquareWarning, XCircle, Eye, Download } from "lucide-react";
import { MemoryBookStatusBadge, RoadTo2027Badge } from "@/components/MemoryBookStatusBadge";
import { MemoryBookViewer } from "@/components/MemoryBookViewer";
import api from "@/lib/api";

export default function CopfMemoryBooksPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState(null);
  const [openData, setOpenData] = useState(null);
  const [decision, setDecision] = useState(null); // "approve" | "request_changes" | "reject"
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  const refresh = async () => {
    setLoading(true);
    try {
      const res = await api.get("/copf/memory-books/pending");
      setItems(res.data?.items || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, []);

  const openVersion = async (versionId) => {
    setOpenId(versionId);
    setOpenData(null);
    try {
      const res = await api.get(`/copf/memory-books/${versionId}`);
      setOpenData(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec chargement");
    }
  };

  const submitDecision = async () => {
    if (!openId || !decision) return;
    if ((decision === "request_changes" || decision === "reject") && !notes.trim()) {
      toast.error("Un commentaire est requis.");
      return;
    }
    setBusy(true);
    try {
      await api.post(`/copf/memory-books/${openId}/decision`, { decision, notes: notes || null });
      toast.success("Décision enregistrée");
      setOpenId(null); setOpenData(null); setNotes(""); setDecision(null);
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec de la décision");
    } finally { setBusy(false); }
  };

  return (
    <div className="max-w-6xl mx-auto p-4 md:p-6 space-y-6" data-testid="copf-memory-books-page">
      <div>
        <h1 className="text-2xl md:text-3xl font-display font-semibold">Livrets à agréer</h1>
        <p className="text-sm text-muted-foreground">
          Vous validez ou refusez l'agrément institutionnel. Vous ne modifiez pas le contenu directement.
        </p>
      </div>

      {loading ? (
        <p className="text-sm text-muted-foreground">Chargement…</p>
      ) : items.length === 0 ? (
        <Card>
          <CardContent className="p-6 text-center text-sm text-muted-foreground">
            Aucun livret en attente d'agrément.
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {items.map(({ version, request }) => (
            <Card key={version.id} className="aito-card-hover" data-testid={`copf-memorybook-row-${version.id}`}>
              <CardContent className="p-4 flex flex-col md:flex-row md:items-center justify-between gap-3">
                <div className="space-y-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium">{version.title}</span>
                    <MemoryBookStatusBadge status={version.status} />
                    {request?.road_to_2027 && <RoadTo2027Badge />}
                  </div>
                  <p className="text-xs font-mono text-muted-foreground">
                    v{version.version_number} · finalisé le {new Date(version.finalized_at).toLocaleDateString("fr-FR")}
                    {request?.period_year ? ` · Période ${request.period_year}` : ""}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" onClick={() => openVersion(version.id)} data-testid={`copf-open-${version.id}`}>
                    <Eye className="h-4 w-4 mr-1" /> Consulter
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={!!openId} onOpenChange={(o) => { if (!o) { setOpenId(null); setOpenData(null); setNotes(""); setDecision(null); } }}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Livret à agréer</DialogTitle>
            <DialogDescription>Consultez le livret puis prenez une décision.</DialogDescription>
          </DialogHeader>

          {!openData ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" /> Chargement…
            </div>
          ) : (
            <div className="space-y-4">
              <MemoryBookViewer version={openData.version} snapshot={openData.snapshot} />

              <Separator />

              <div className="space-y-3">
                <label className="text-sm font-medium">Commentaire (obligatoire pour refus / corrections)</label>
                <Textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="copf-decision-notes" />
              </div>
            </div>
          )}

          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button
              variant="outline"
              className="border-[hsl(var(--destructive))] text-[hsl(var(--destructive))]"
              disabled={busy}
              onClick={() => { setDecision("reject"); }}
              data-testid="copf-reject-button"
            >
              <XCircle className="h-4 w-4 mr-2" /> Refuser
            </Button>
            <Button
              variant="outline"
              disabled={busy}
              onClick={() => { setDecision("request_changes"); }}
              data-testid="copf-request-changes-button"
            >
              <MessageSquareWarning className="h-4 w-4 mr-2" /> Demander corrections
            </Button>
            <Button
              disabled={busy}
              onClick={() => { setDecision("approve"); }}
              data-testid="copf-approve-button"
            >
              <CheckCircle2 className="h-4 w-4 mr-2" /> Approuver
            </Button>
          </DialogFooter>

          {decision && (
            <div className="rounded-md border p-3 flex items-center justify-between mt-2">
              <span className="text-sm">
                Confirmer : <b>{decision === "approve" ? "Approuver" : decision === "request_changes" ? "Demander corrections" : "Refuser"}</b>
              </span>
              <Button size="sm" disabled={busy} onClick={submitDecision} data-testid="copf-confirm-decision-button">
                {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                Confirmer
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
